
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {CommissionMode} from './CommissionMode';
import {FieldModelCommissionMode} from './CommissionMode';
import {CommissionChargingType} from './CommissionChargingType';
import {FieldModelCommissionChargingType} from './CommissionChargingType';
import {CommissionValue} from './CommissionValue';
import {FieldModelCommissionValue} from './CommissionValue';
import {CommissionCalculationType} from './CommissionCalculationType';
import {FieldModelCommissionCalculationType} from './CommissionCalculationType';
import {CommissionType} from './CommissionType';
import {FieldModelCommissionType} from './CommissionType';

/**
 * CommissionProfile struct
 */
class CommissionProfile {
  /**
   * Initialize struct
   * @param {!UUID=} id
   * @param {!string=} name
   * @param {!string=} comment
   * @param {!string=} description
   * @param {!CommissionMode=} mode
   * @param {!CommissionChargingType=} chargingType
   * @param {!Array=} values
   * @param {!CommissionCalculationType=} calculationType
   * @param {!CommissionType=} type
   * @constructor
   */
  constructor (id = UUID.sequential(), name = '', comment = '', description = '', mode = new CommissionMode(), chargingType = new CommissionChargingType(), values = [], calculationType = new CommissionCalculationType(), type = new CommissionType()) {
    this.id = id
    this.Name = name
    this.Comment = comment
    this.Description = description
    this.Mode = mode
    this.ChargingType = chargingType
    this.Values = values
    this.CalculationType = calculationType
    this.Type = type
  }

  /**
   * Copy struct (shallow copy)
   * @this {!CommissionProfile}
   * @param {!CommissionProfile} other Other struct
   * @returns {!CommissionProfile} This struct
   */
  copy (other) {
    if (other.id != null) {
      this.id = new UUID(other.id)
    } else {
      this.id = null
    }
    if (other.Name != null) {
      this.Name = other.Name
    } else {
      this.Name = null
    }
    if (other.Comment != null) {
      this.Comment = other.Comment
    } else {
      this.Comment = null
    }
    if (other.Description != null) {
      this.Description = other.Description
    } else {
      this.Description = null
    }
    if (other.Mode != null) {
      this.Mode = CommissionMode.fromObject(other.Mode)
    } else {
      this.Mode = null
    }
    if (other.ChargingType != null) {
      this.ChargingType = CommissionChargingType.fromObject(other.ChargingType)
    } else {
      this.ChargingType = null
    }
    if (other.Values != null) {
      this.Values = []
      for (let item of other.Values) {
        if (item != null) {
          let tempItem
          tempItem = CommissionValue.fromObject(item)
          this.Values.push(tempItem)
        } else {
          this.Values.push(null)
        }
      }
    } else {
      this.Values = null
    }
    if (other.CalculationType != null) {
      this.CalculationType = CommissionCalculationType.fromObject(other.CalculationType)
    } else {
      this.CalculationType = null
    }
    if (other.Type != null) {
      this.Type = CommissionType.fromObject(other.Type)
    } else {
      this.Type = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!CommissionProfile}
   * @returns {!CommissionProfile} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new CommissionProfileModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new CommissionProfileModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!CommissionProfile}
   * @param {!CommissionProfile} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof CommissionProfile)) {
      throw new TypeError('Instance of CommissionProfile is required!')
    }
    // noinspection RedundantIfStatementJS
    if (this.id != null) {
      if ((other.id == null) || !this.id.eq(other.id)) {
        return false
      }
    } else if (other.id != null) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!CommissionProfile}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      id: ((this.id != null) ? this.id.toString() : null),
      Name: ((this.Name != null) ? this.Name : null),
      Comment: ((this.Comment != null) ? this.Comment : null),
      Description: ((this.Description != null) ? this.Description : null),
      Mode: ((this.Mode != null) ? this.Mode : null),
      ChargingType: ((this.ChargingType != null) ? this.ChargingType : null),
      Values: ((this.Values != null) ? Array.from(this.Values, item => ((item != null) ? item : null)) : null),
      CalculationType: ((this.CalculationType != null) ? this.CalculationType : null),
      Type: ((this.Type != null) ? this.Type : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return CommissionProfile.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!CommissionProfile} other Object value
   * @returns {!CommissionProfile} Created struct
   */
  static fromObject (other) {
    return new CommissionProfile().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!CommissionProfile}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return CommissionProfile.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!CommissionProfile}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 21
  }
}

export { CommissionProfile };

/**
 * Fast Binary Encoding CommissionProfile field model
 */
class FieldModelCommissionProfile extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.id = new fbe.FieldModelUUID(buffer, 4 + 4)
    this.Name = new fbe.FieldModelString(buffer, this.id.fbeOffset + this.id.fbeSize)
    this.Comment = new fbe.FieldModelString(buffer, this.Name.fbeOffset + this.Name.fbeSize)
    this.Description = new fbe.FieldModelString(buffer, this.Comment.fbeOffset + this.Comment.fbeSize)
    this.Mode = new FieldModelCommissionMode(buffer, this.Description.fbeOffset + this.Description.fbeSize)
    this.ChargingType = new FieldModelCommissionChargingType(buffer, this.Mode.fbeOffset + this.Mode.fbeSize)
    this.Values = new fbe.FieldModelVector(new FieldModelCommissionValue(buffer, this.ChargingType.fbeOffset + this.ChargingType.fbeSize), buffer, this.ChargingType.fbeOffset + this.ChargingType.fbeSize)
    this.CalculationType = new FieldModelCommissionCalculationType(buffer, this.Values.fbeOffset + this.Values.fbeSize)
    this.Type = new FieldModelCommissionType(buffer, this.CalculationType.fbeOffset + this.CalculationType.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.id.fbeSize + this.Name.fbeSize + this.Comment.fbeSize + this.Description.fbeSize + this.Mode.fbeSize + this.ChargingType.fbeSize + this.Values.fbeSize + this.CalculationType.fbeSize + this.Type.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.id.fbeExtra + this.Name.fbeExtra + this.Comment.fbeExtra + this.Description.fbeExtra + this.Mode.fbeExtra + this.ChargingType.fbeExtra + this.Values.fbeExtra + this.CalculationType.fbeExtra + this.Type.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelCommissionProfile.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 21
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelCommissionProfile}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelCommissionProfile}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Name.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Comment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.Mode.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Mode.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Mode.fbeSize

    if ((fbeCurrentSize + this.ChargingType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ChargingType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChargingType.fbeSize

    if ((fbeCurrentSize + this.Values.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Values.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Values.fbeSize

    if ((fbeCurrentSize + this.CalculationType.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.CalculationType.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CalculationType.fbeSize

    if ((fbeCurrentSize + this.Type.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Type.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Type.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelCommissionProfile}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelCommissionProfile}
   * @param {!CommissionProfile} fbeValue Default value, defaults is new CommissionProfile()
   * @returns {!CommissionProfile} CommissionProfile value
   */
  get (fbeValue = new CommissionProfile()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelCommissionProfile}
   * @param {!CommissionProfile} fbeValue CommissionProfile value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.id.fbeSize) <= fbeStructSize) {
      fbeValue.id = this.id.get(UUID.sequential())
    } else {
      fbeValue.id = UUID.sequential()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.id.fbeSize

    if ((fbeCurrentSize + this.Name.fbeSize) <= fbeStructSize) {
      fbeValue.Name = this.Name.get()
    } else {
      fbeValue.Name = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Name.fbeSize

    if ((fbeCurrentSize + this.Comment.fbeSize) <= fbeStructSize) {
      fbeValue.Comment = this.Comment.get()
    } else {
      fbeValue.Comment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Comment.fbeSize

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.Mode.fbeSize) <= fbeStructSize) {
      fbeValue.Mode = this.Mode.get()
    } else {
      fbeValue.Mode = new CommissionMode()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Mode.fbeSize

    if ((fbeCurrentSize + this.ChargingType.fbeSize) <= fbeStructSize) {
      fbeValue.ChargingType = this.ChargingType.get()
    } else {
      fbeValue.ChargingType = new CommissionChargingType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ChargingType.fbeSize

    if ((fbeCurrentSize + this.Values.fbeSize) <= fbeStructSize) {
      this.Values.get(fbeValue.Values)
    } else {
      fbeValue.Values.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Values.fbeSize

    if ((fbeCurrentSize + this.CalculationType.fbeSize) <= fbeStructSize) {
      fbeValue.CalculationType = this.CalculationType.get()
    } else {
      fbeValue.CalculationType = new CommissionCalculationType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.CalculationType.fbeSize

    if ((fbeCurrentSize + this.Type.fbeSize) <= fbeStructSize) {
      fbeValue.Type = this.Type.get()
    } else {
      fbeValue.Type = new CommissionType()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Type.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelCommissionProfile}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelCommissionProfile}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelCommissionProfile}
   * @param {!CommissionProfile} fbeValue CommissionProfile value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelCommissionProfile}
   * @param {CommissionProfile} fbeValue CommissionProfile value
   */
  setFields (fbeValue) {
    this.id.set(fbeValue.id)
    this.Name.set(fbeValue.Name)
    this.Comment.set(fbeValue.Comment)
    this.Description.set(fbeValue.Description)
    this.Mode.set(fbeValue.Mode)
    this.ChargingType.set(fbeValue.ChargingType)
    this.Values.set(fbeValue.Values)
    this.CalculationType.set(fbeValue.CalculationType)
    this.Type.set(fbeValue.Type)
  }
}

export { FieldModelCommissionProfile };

/**
 * Fast Binary Encoding CommissionProfile model
 */
class CommissionProfileModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelCommissionProfile(this.buffer, 4)
  }

  /**
   * Get the CommissionProfile model
   * @this {!CommissionProfileModel}
   * @returns {!FieldModelCommissionProfile} model CommissionProfile model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!CommissionProfileModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!CommissionProfileModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return CommissionProfileModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!CommissionProfileModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelCommissionProfile.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!CommissionProfileModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!CommissionProfileModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!CommissionProfileModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!CommissionProfileModel}
   * @param {!CommissionProfile} value CommissionProfile value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!CommissionProfileModel}
   * @param {!CommissionProfile} value CommissionProfile value, defaults is new CommissionProfile()
   * @return {!object} Deserialized CommissionProfile value and its size
   */
  deserialize (value = new CommissionProfile()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new CommissionProfile(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new CommissionProfile(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!CommissionProfileModel}
   * @param {!number} prev Previous CommissionProfile model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { CommissionProfileModel };
